import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import api from "../api/axios";
import {
  Eye,
  Upload,
  Search,
  FileX,
  FileCheck,
  X,
  Edit,
  Loader2,
  User,
  Phone,
  Mail,
  MapPin,
  AlertCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Repeat,
  Send,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Lock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ProviderTransferModal from "./ProviderTransferModal"; // naya component — niche note dekho
import StatsDashboard from "./StatsDashboard";
import { useSearchParams } from "react-router-dom";
import jsPDF from "jspdf";

const API_PATH = "/admin/border-tax";
const TRANSFER_API_PATH = "/provider"; // matches: /api/provider/*
const DRIVER_API = "https://authapi.taxisafar.com/api/v1/driver-details";

const DEFAULT_WA_MESSAGE =
  "आपका Tax Government Side से Pending है, इसे Complete होने में कुछ Extra Time लग सकता है।";

const normalizePhone = (num) => {
  if (!num) return null;
  let digits = String(num).replace(/\D/g, "");
  if (digits.length === 10) digits = "91" + digits;
  return digits;
};
const openWhatsapp = async (
  driverId,
  fallbackContact,
  defaultMsg = DEFAULT_WA_MESSAGE,
) => {
  let contact = fallbackContact;

  if (driverId) {
    try {
      const res = await axios.get(`${DRIVER_API}/${driverId}`);
      contact = res.data?.data?.driver_contact_number || fallbackContact;
    } catch (err) {
      // fetch fail ho gaya to fallback contact hi use hoga
    }
  }

  const phone = normalizePhone(contact);
  if (!phone) {
    Swal.fire({ icon: "warning", title: "Driver number not found" });
    return;
  }

  const { value: message, isConfirmed } = await Swal.fire({
    title: "Send WhatsApp Message",
    input: "textarea",
    inputValue: defaultMsg,
    inputAttributes: { rows: 4 },
    showCancelButton: true,
    confirmButtonText: "Open WhatsApp",
  });
  if (!isConfirmed) return;

  const url = `https://wa.me/${phone}?text=${encodeURIComponent(message || "")}`;
  window.open(url, "_blank");
};

const WhatsappIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12.001 2.003c-5.514 0-9.997 4.483-9.997 9.997 0 1.762.464 3.484 1.346 4.997L2.06 22l5.13-1.264a9.958 9.958 0 004.811 1.225h.001c5.514 0 9.997-4.483 9.997-9.997 0-2.67-1.039-5.18-2.926-7.067A9.933 9.933 0 0012.001 2.003zm0 18.045c-1.55 0-3.05-.416-4.36-1.203l-.313-.187-3.045.75.813-2.968-.204-.305a8.028 8.028 0 01-1.24-4.278c0-4.454 3.626-8.08 8.08-8.08a8.03 8.03 0 015.71 2.365 8.032 8.032 0 012.37 5.716c0 4.454-3.627 8.08-8.08 8.08z" />
  </svg>
);
const REASON_OPTIONS = [
  "Tax Already Paid",
  "Vehicle Documents Not Complete – Payment Refund",
  "Road Tax Expired – Payment Refund",
  "Government Server Down – Payment Refund",
  "Government Site Pending – Please Wait 15 More Minutes",
  "Government Site Pending – Please Wait 30 More Minutes",
  "Other",
];

const imageFileToPdf = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const pdf = new jsPDF({
          orientation: img.width > img.height ? "landscape" : "portrait",
          unit: "px",
          format: [img.width, img.height],
        });
        pdf.addImage(img, "JPEG", 0, 0, img.width, img.height);
        const blob = pdf.output("blob");
        resolve(
          new File([blob], file.name.replace(/\.[^.]+$/, "") + ".pdf", {
            type: "application/pdf",
          }),
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtAmt = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const STATUS_COLORS = {
  active: "bg-green-100 text-green-800 border-green-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  expired: "bg-red-100 text-red-800 border-red-200",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
  failed: "bg-gray-200 text-gray-700 border-gray-300",
};
const STATUS_OPTIONS = [
  "pending",
  "active",
  "verified",
  "expired",
  "failed",
  "cancelled",
];

const PAYMENT_COLORS = {
  paid: "bg-green-100 text-green-800 border-green-200",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

const statusCls = (s) =>
  STATUS_COLORS[(s || "").toLowerCase()] ||
  "bg-gray-100 text-gray-700 border-gray-200";
const paymentCls = (s) =>
  PAYMENT_COLORS[(s || "").toLowerCase()] ||
  "bg-gray-100 text-gray-700 border-gray-200";

// ─── SMALL COMPONENTS ────────────────────────────────────────────────────────

const Badge = ({ label, cls }) => (
  <span
    className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}
  >
    {label}
  </span>
);

const ReassignedBadge = ({ title }) => (
  <span
    title={title}
    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-purple-100 text-purple-800 border-purple-200"
  >
    <Repeat size={12} /> Transferred
  </span>
);

const DetailRow = ({ label, value, bold, large, mono }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-gray-100 last:border-0">
    <span className="text-gray-500 text-sm">{label}</span>
    <span
      className={`${bold ? "font-bold" : "font-medium"} ${large ? "text-lg" : "text-sm"} ${mono ? "font-mono" : ""} text-gray-900`}
    >
      {value ?? "—"}
    </span>
  </div>
);

const InfoCard = ({ title, children, span2 }) => (
  <div
    className={`bg-gray-50 rounded-xl border p-5 ${span2 ? "md:col-span-2" : ""}`}
  >
    <h4 className="font-semibold text-sm text-gray-500 uppercase tracking-wide mb-4">
      {title}
    </h4>
    <div className="space-y-3">{children}</div>
  </div>
);

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    {icon && <div className="text-indigo-500 mt-0.5 shrink-0">{icon}</div>}
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 break-words">
        {value || "—"}
      </p>
    </div>
  </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const AllBorderTax = () => {
  const { employee } = useAuth();
  const myNumber = employee?.phone;
  const [docType, setDocType] = useState("border_tax"); // "border_tax" | "refund"

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [transferOrder, setTransferOrder] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [pdfModalOrder, setPdfModalOrder] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [validTill, setValidTill] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const [analyticsOpen, setAnalyticsOpen] = useState(false); // default closed
  // "assigned" => hit /assigned-to-me (jo mujhe transfer se mila)
  // default => hit /provider (jo abhi current me mera h, transferred-away wale exclude ho jayenge automatically)
  const onlyShow = searchParams.get("onlyshow"); // null | "assignedtome" | "transferredbyme"

  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [driverData, setDriverData] = useState(null);
  const [driverLoading, setDriverLoading] = useState(false);
  const [driverError, setDriverError] = useState(null);

  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);

  const [statusFilter, setStatusFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [daysFilter, setDaysFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // NOTE: provider_number query param hata diya — JWT se hi decide hota h ab backend pe
  const filterParams = {
    status: statusFilter || undefined,
    state: stateFilter || undefined,
    paymentStatus: paymentStatusFilter || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  // ── Fetch stats ──────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get(`${API_PATH}/orders/stats-employe`, {
        params: filterParams,
      });
      if (res.data.success) setStats(res.data.data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to load stats",
        text: err.response?.data?.message || "Check connection",
      });
    } finally {
      setStatsLoading(false);
    }
  }, [statusFilter, stateFilter, paymentStatusFilter, startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  const switchView = (view) => {
    if (view) {
      setSearchParams({ onlyshow: view });
    } else {
      setSearchParams({});
    }
    setPage(1);
  };
  // ── Fetch orders ─────────────────────────────────────────────────────────
  const fetchOrders = useCallback(
    async (currentPage = 1) => {
      try {
        setLoading(true);

        const params = {
          ...filterParams,
          page: currentPage,
          limit,
          search: search.trim() || undefined,
          days: daysFilter || undefined,
        };

        // route decide: assigned-to-me (transfer se mila) vs normal (current owner)
        const url =
          onlyShow === "assignedtome"
            ? `${TRANSFER_API_PATH}/get-orders/assigned-to-me`
            : onlyShow === "transferredbyme"
              ? `${TRANSFER_API_PATH}/get-orders/transferred-by-me`
              : `${API_PATH}/orders/admin/provider`;

        const res = await api.get(url, { params });

        if (res.data.success) {
          let data = [...res.data.data].sort((a, b) => {
            let vA = a[sortConfig.key];
            let vB = b[sortConfig.key];

            if (sortConfig.key === "createdAt") {
              vA = new Date(vA);
              vB = new Date(vB);
            } else if (sortConfig.key === "payment.total_amount") {
              vA = Number(a.payment?.total_amount || 0);
              vB = Number(b.payment?.total_amount || 0);
            } else if (sortConfig.key === "days") {
              vA = Number(a.days || 0);
              vB = Number(b.days || 0);
            }

            if (vA < vB) return sortConfig.direction === "asc" ? -1 : 1;
            if (vA > vB) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
          });

          setOrders(data);
          setTotalPages(res.data.meta?.totalPages || 1);
          setTotalOrders(res.data.meta?.total ?? data.length);
          setPage(currentPage);
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Failed to load orders",
          text: err.response?.data?.message || "Check connection",
        });
      } finally {
        setLoading(false);
      }
    },
    [
      search,
      statusFilter,
      stateFilter,
      paymentStatusFilter,
      daysFilter,
      startDate,
      endDate,
      sortConfig,
      onlyShow,
    ],
  );

  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-3 w-3 inline ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 inline ml-1" />
    );
  };

  const clearFilters = () => {
    setStatusFilter("");
    setStateFilter("");
    setPaymentStatusFilter("");
    setDaysFilter("");
    setStartDate("");
    setEndDate("");
    setSearch("");
  };
  const hasActiveFilters =
    statusFilter ||
    stateFilter ||
    paymentStatusFilter ||
    daysFilter ||
    startDate ||
    endDate ||
    search;

  const downloadReport = async () => {
    const p = new URLSearchParams();
    if (startDate) p.append("startDate", startDate);
    if (endDate) p.append("endDate", endDate);
    if (statusFilter) p.append("status", statusFilter);

    try {
      const res = await api.get(`${API_PATH}/download-report?${p.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "border-tax-report.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Download failed",
        text: err?.response?.data?.message || "Could not download report",
      });
    }
  };

  const showDriverInfo = async (driverId) => {
    if (!driverId) {
      Swal.fire({ icon: "info", title: "No driver ID" });
      return;
    }
    setDriverModalOpen(true);
    setDriverLoading(true);
    setDriverError(null);
    setDriverData(null);
    try {
      const res = await axios.get(`${DRIVER_API}/${driverId}`);
      setDriverData(res.data?.data || null);
    } catch (err) {
      setDriverError(err.response?.data?.message || "Failed to fetch driver");
    } finally {
      setDriverLoading(false);
    }
  };

  const changeStatus = async (orderId, newStatus) => {
    const { value: selected, isConfirmed: step1Confirmed } = await Swal.fire({
      title: `Change status to ${newStatus}?`,
      input: "select",
      inputLabel: "Reason",
      inputPlaceholder: "Select a reason",
      inputOptions: REASON_OPTIONS.reduce((acc, r) => {
        acc[r] = r;
        return acc;
      }, {}),
      inputValidator: (v) => (!v ? "Reason is required" : undefined),
      showCancelButton: true,
      confirmButtonColor: newStatus === "cancelled" ? "#dc2626" : "#2563eb",
      confirmButtonText: "Next",
    });

    if (!step1Confirmed || !selected) return;

    let reason = selected;

    if (selected === "Other") {
      const { value: customReason, isConfirmed: step2Confirmed } =
        await Swal.fire({
          title: "Enter reason",
          input: "text",
          inputLabel: "Reason",
          inputPlaceholder: "Why is this status changing?",
          inputValidator: (v) => (!v ? "Reason is required" : undefined),
          showCancelButton: true,
          confirmButtonColor: newStatus === "cancelled" ? "#dc2626" : "#2563eb",
          confirmButtonText: "Update",
        });

      if (!step2Confirmed || !customReason) return;
      reason = customReason;
    }

    try {
      await api.post(`${API_PATH}/orders/status-changes/${orderId}`, {
        status: newStatus,
        reason,
      });
      Swal.fire("Updated", "Status changed", "success");
      fetchOrders(page);
      fetchStats();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Failed", "error");
    }
  };

  const showNoAccess = () => {
    Swal.fire({
      icon: "warning",
      title: "No access",
      text: "This order is not assigned to you, so you can't take action on it.",
      confirmButtonColor: "#2563eb",
    });
  };

  const handleUploadPdf = async () => {
    if (!pdfFile) return Swal.fire("Required", "Select a file", "warning");

    let fileToUpload = pdfFile;

    if (pdfFile.type.startsWith("image/")) {
      setConverting(true);
      try {
        fileToUpload = await imageFileToPdf(pdfFile);
      } catch (err) {
        setConverting(false);
        return Swal.fire("Error", "Failed to convert image to PDF", "error");
      }
      setConverting(false);
    }

    setUploading(true);
    const fd = new FormData();
    fd.append("orderId", pdfModalOrder.id);
    fd.append("pdf", fileToUpload);
    fd.append("docType", docType);

    if (validTill) fd.append("validTill", validTill);
    try {
      await api.post(`${API_PATH}/order/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire("Uploaded", "PDF uploaded", "success");
      setPdfModalOrder(null);
      setPdfFile(null);
      setValidTill("");
      fetchOrders(page);
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Upload failed",
        "error",
      );
    } finally {
      setUploading(false);
    }
  };

  // ── View Modal ────────────────────────────────────────────────────────────
  const ViewModal = () => {
    if (!selectedOrder) return null;
    const pay = selectedOrder.payment || {};
    const hasPdf = !!selectedOrder.pdf?.url;
    const isPending =
      (selectedOrder.status_of_Tax || "").toLowerCase() === "pending";
    const wasReceivedViaTransfer = !!selectedOrder.wasReceivedViaTransfer;
    const lastTransfer =
      selectedOrder.transfer_history?.[
        selectedOrder.transfer_history.length - 1
      ] || null;

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white px-6 py-4 border-b flex justify-between items-center">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Order details</h2>
              {wasReceivedViaTransfer && (
                <ReassignedBadge
                  title={`Received via transfer${lastTransfer ? ` from ${lastTransfer.from_provider}` : ""}`}
                />
              )}
            </div>
            <button
              onClick={() => setSelectedOrder(null)}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-6 grid md:grid-cols-2 gap-6">
            <div className="space-y-5">
              <div className="bg-gray-50 rounded-xl border p-5">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
                  Order info
                </h3>
                <DetailRow label="Order ID" value={selectedOrder.id} mono />
                <DetailRow label="State" value={selectedOrder.state} />
                <DetailRow label="Border" value={selectedOrder.border} />
                <DetailRow label="Vehicle" value={selectedOrder.vehicle} />
                <DetailRow
                  label="Vehicle No."
                  value={selectedOrder.vehicleNumber?.toUpperCase()}
                  mono
                />
                <DetailRow label="Trip type" value={selectedOrder.tripType} />
                <DetailRow
                  label="Tax mode"
                  value={selectedOrder.taxMode || "—"}
                />
                <DetailRow
                  label="Days"
                  value={selectedOrder.days || selectedOrder.numDays || "—"}
                />
                <DetailRow
                  label="Period"
                  value={`${fmtDateTime(selectedOrder.startDate)} → ${fmtDateTime(selectedOrder.endDate)}`}
                />
                <DetailRow
                  label="Created"
                  value={fmtDateTime(selectedOrder.createdAt)}
                />
                <DetailRow
                  label="Current provider"
                  value={selectedOrder.provider_number}
                  mono
                />
              </div>

              {selectedOrder.transfer_history?.length > 0 && (
                <div className="bg-purple-50 rounded-xl border border-purple-100 p-5">
                  <h3 className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-3">
                    Transfer history
                  </h3>
                  <div className="space-y-2">
                    {selectedOrder.transfer_history.map((t, i) => (
                      <div
                        key={i}
                        className="text-xs text-gray-600 flex justify-between border-b border-purple-100 pb-1.5 last:border-0"
                      >
                        <span className="font-mono">
                          {t.from_provider} → {t.to_provider}
                        </span>
                        <span>{fmtDateTime(t.transferred_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl border p-5">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
                  PDF status
                </h3>
                {hasPdf ? (
                  <div className="flex items-center gap-3 text-green-700">
                    <FileCheck size={20} />
                    <div>
                      <p className="font-medium text-sm">PDF uploaded</p>
                      <a
                        href={selectedOrder.pdf.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        View / download PDF
                      </a>
                      {selectedOrder.pdf.is_expired && (
                        <p className="text-xs text-red-500 mt-0.5">
                          Expired on {fmtDate(selectedOrder.pdf.expired_time)}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-amber-600">
                    <FileX size={20} />
                    <span className="text-sm font-medium">
                      No PDF uploaded yet
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-5">
              <div className="bg-blue-50 rounded-xl border border-blue-100 p-5">
                <h3 className="text-xs font-semibold text-blue-400 uppercase tracking-wide mb-4">
                  Pricing breakdown
                </h3>
                {pay.frontend_breakdown ? (
                  <>
                    <DetailRow
                      label="Base price"
                      value={fmtAmt(pay.frontend_breakdown.basePrice)}
                    />
                    <DetailRow
                      label="Platform fee"
                      value={fmtAmt(pay.frontend_breakdown.platformFee)}
                    />
                    <DetailRow
                      label="Gateway fee"
                      value={fmtAmt(pay.frontend_breakdown.gatewayFee)}
                    />
                    <DetailRow
                      label="Calculation mode"
                      value={pay.frontend_breakdown.calculationMode}
                    />
                    <div className="pt-3 border-t border-blue-200 mt-2">
                      <DetailRow
                        label="Total"
                        value={fmtAmt(
                          pay.frontend_breakdown.total ?? pay.total_amount,
                        )}
                        bold
                        large
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <DetailRow
                      label="Base price"
                      value={fmtAmt(pay.base_price)}
                    />
                    {pay.slab_days && (
                      <DetailRow
                        label="Slab"
                        value={`${pay.slab_days} days @ ₹${pay.slab_price || 0}`}
                      />
                    )}
                    {pay.extra_days > 0 && (
                      <DetailRow
                        label="Extra days"
                        value={`${pay.extra_days} × ₹${((pay.extra_days_charge || 0) / pay.extra_days).toFixed(0)} = ${fmtAmt(pay.extra_days_charge)}`}
                      />
                    )}
                    {pay.platform?.platform_total > 0 && (
                      <DetailRow
                        label="Platform fee"
                        value={`${fmtAmt(pay.platform.platform_total)} (₹${pay.platform.fee || 0} + ${pay.platform.gateway_fee || 0}%)`}
                      />
                    )}
                    <div className="pt-3 border-t border-blue-200 mt-2">
                      <DetailRow
                        label="Total"
                        value={fmtAmt(pay.total_amount)}
                        bold
                        large
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl border p-5">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
                  Status
                </h3>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Tax status</span>
                    <Badge
                      label={selectedOrder.status_of_Tax || "Unknown"}
                      cls={statusCls(selectedOrder.status_of_Tax)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Payment</span>
                    <Badge
                      label={selectedOrder.payment_status || "Unknown"}
                      cls={paymentCls(selectedOrder.payment_status)}
                    />
                  </div>
                </div>

                {selectedOrder.canEdit !== false && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <label className="text-xs text-gray-500 block mb-1.5">
                      Change status
                    </label>
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        changeStatus(selectedOrder.id, e.target.value);
                        e.target.value = "";
                      }}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="" disabled>
                        Select new status…
                      </option>
                      {STATUS_OPTIONS.filter(
                        (s) =>
                          s !==
                          (selectedOrder.status_of_Tax || "").toLowerCase(),
                      ).map((s) => (
                        <option key={s} value={s}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t bg-gray-50 flex flex-wrap gap-3 justify-end">
            {isPending && selectedOrder.canEdit !== false && (
              <>
                <button
                  onClick={() =>
                    Swal.fire(
                      "Coming soon",
                      "Edit functionality coming soon",
                      "info",
                    )
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600"
                >
                  <Edit size={16} /> Edit
                </button>
                <button
                  onClick={() => changeStatus(selectedOrder.id, "cancelled")}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                >
                  <X size={16} /> Cancel order
                </button>
              </>
            )}
            {selectedOrder.canEdit === false && (
              <span className="flex items-center gap-1.5 text-xs text-gray-400 mr-auto">
                <Lock size={14} /> Not assigned to you — view only
              </span>
            )}
            <button
              onClick={() => setSelectedOrder(null)}
              className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Driver Modal (unchanged) ─────────────────────────────────────────────
  const DriverModal = () => {
    if (!driverModalOpen) return null;
    const closeDriver = () => {
      setDriverModalOpen(false);
      setDriverData(null);
      setDriverError(null);
    };
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white px-6 py-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <User size={20} className="text-indigo-500" /> Driver details
            </h2>
            <button
              onClick={closeDriver}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-6">
            {driverLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2
                  size={40}
                  className="animate-spin text-indigo-500 mb-3"
                />
                <p className="text-gray-500">Fetching driver…</p>
              </div>
            ) : driverError ? (
              <div className="text-center py-12 text-red-500">
                <AlertCircle size={48} className="mx-auto mb-4" />
                <p>{driverError}</p>
              </div>
            ) : driverData ? (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 pb-6 border-b">
                  {driverData.profile_photo?.url ? (
                    <img
                      src={driverData.profile_photo.url}
                      alt={driverData.driver_name}
                      className="w-20 h-20 rounded-full object-cover border-4 border-indigo-100"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-indigo-50 border-4 border-indigo-100 flex items-center justify-center">
                      <User size={36} className="text-indigo-400" />
                    </div>
                  )}
                  <div className="text-center sm:text-left">
                    <h3 className="text-2xl font-bold">
                      {driverData.driver_name || "Unknown"}
                    </h3>
                    <p className="text-gray-500 font-mono mt-0.5">
                      {driverData.driver_contact_number}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge
                        label={
                          driverData.account_status?.toUpperCase() || "UNKNOWN"
                        }
                        cls={
                          driverData.account_status === "active"
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-red-100 text-red-800 border-red-200"
                        }
                      />
                      {driverData.aadhar_verified && (
                        <Badge
                          label="Aadhaar verified"
                          cls="bg-blue-100 text-blue-800 border-blue-200"
                        />
                      )}
                      <Badge
                        label={driverData.is_online ? "Online" : "Offline"}
                        cls={
                          driverData.is_online
                            ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                            : "bg-gray-100 text-gray-600 border-gray-200"
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoCard title="Contact & personal">
                    <InfoRow
                      icon={<Phone size={16} />}
                      label="Phone"
                      value={driverData.driver_contact_number}
                    />
                    <InfoRow
                      icon={<Mail size={16} />}
                      label="Email"
                      value={driverData.driver_email}
                    />
                    <InfoRow
                      icon={<Calendar size={16} />}
                      label="Date of birth"
                      value={
                        driverData.driver_dob
                          ? fmtDate(driverData.driver_dob)
                          : "—"
                      }
                    />
                    <InfoRow
                      icon={<MapPin size={16} />}
                      label="Address"
                      value={driverData.address}
                    />
                  </InfoCard>
                  <InfoCard title="Activity & stats">
                    <InfoRow
                      label="Total rides"
                      value={driverData.total_rides || 0}
                    />
                    <InfoRow
                      label="Completed rides"
                      value={driverData.completed_rides || 0}
                    />
                    <InfoRow
                      label="Avg. rating"
                      value={
                        driverData.average_rating
                          ? `${driverData.average_rating} ★`
                          : "No ratings"
                      }
                    />
                    <InfoRow
                      label="Referral ID"
                      value={driverData.referral_id}
                    />
                    <InfoRow
                      label="Radius"
                      value={`${driverData.currentRadius || "?"} km`}
                    />
                  </InfoCard>
                  {driverData.current_location?.coordinates && (
                    <InfoCard title="Last known location" span2>
                      <p className="text-sm text-gray-600">
                        Lat: {driverData.current_location.coordinates[1]},&nbsp;
                        Lng: {driverData.current_location.coordinates[0]}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Updated: {fmtDateTime(driverData.lastLocationUpdate)}
                      </p>
                    </InfoCard>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-center py-12 text-gray-400">
                No driver information available
              </p>
            )}
          </div>

          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
            <button
              onClick={closeDriver}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const dispStart = (page - 1) * limit + 1;
  const dispEnd = Math.min(page * limit, totalOrders);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Border tax orders
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {fmt(totalOrders)} total orders
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchOrders(page)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 bg-white rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={downloadReport}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition"
            >
              <Download className="h-4 w-4" /> Export Excel
            </button>
          </div>
        </div>

        {/* Tabs — My orders / Assigned to me / Transferred by me */}
        <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-1.5 w-fit shadow-sm">
          <button
            onClick={() => switchView(null)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${
              !onlyShow
                ? "bg-indigo-600 text-white shadow"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            My orders
          </button>
          <button
            onClick={() => switchView("assignedtome")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${
              onlyShow === "assignedtome"
                ? "bg-purple-600 text-white shadow"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            <Repeat size={14} /> Assigned to me
          </button>
          <button
            onClick={() => switchView("transferredbyme")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${
              onlyShow === "transferredbyme"
                ? "bg-amber-600 text-white shadow"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            <Send size={14} /> Transferred by me
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <div className="relative xl:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search orders…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="verified">Verified</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
            </select>

            <input
              type="text"
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value);
                setPage(1);
              }}
              placeholder="State (e.g. Delhi)"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />

            <select
              value={paymentStatusFilter}
              onChange={(e) => {
                setPaymentStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>

            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />

            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>

          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100 items-center">
              <span className="text-xs text-gray-400">Active:</span>
              {search && (
                <Chip
                  label={`Search: ${search}`}
                  onRemove={() => setSearch("")}
                />
              )}
              {statusFilter && (
                <Chip
                  label={`Status: ${statusFilter}`}
                  onRemove={() => setStatusFilter("")}
                />
              )}
              {stateFilter && (
                <Chip
                  label={`State: ${stateFilter}`}
                  onRemove={() => setStateFilter("")}
                />
              )}
              {paymentStatusFilter && (
                <Chip
                  label={`Payment: ${paymentStatusFilter}`}
                  onRemove={() => setPaymentStatusFilter("")}
                />
              )}
              {startDate && (
                <Chip
                  label={`From: ${fmtDate(startDate)}`}
                  onRemove={() => setStartDate("")}
                />
              )}
              {endDate && (
                <Chip
                  label={`To: ${fmtDate(endDate)}`}
                  onRemove={() => setEndDate("")}
                />
              )}
              <button
                onClick={clearFilters}
                className="text-xs text-red-500 hover:text-red-700 font-medium ml-1"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setAnalyticsOpen((prev) => !prev)}
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <BarChart3 size={16} className="text-indigo-500" />
              Analytics
            </span>
            {analyticsOpen ? (
              <ChevronUp size={18} className="text-gray-400" />
            ) : (
              <ChevronDown size={18} className="text-gray-400" />
            )}
          </button>
          {analyticsOpen && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4">
              <StatsDashboard stats={stats} loading={statsLoading} />
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Loader2
                size={36}
                className="animate-spin text-indigo-500 mb-3"
              />
              Loading orders…
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-20 text-gray-400 text-sm">
              {onlyShow === "assignedtome"
                ? "No orders have been assigned to you via transfer yet."
                : onlyShow === "transferredbyme"
                  ? "You haven't transferred any orders yet."
                  : `No orders found${hasActiveFilters ? " matching your filters" : ""}.`}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1150px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {[
                        { label: "Created", key: "createdAt" },
                        { label: "State" },
                        { label: "Border" },
                        { label: "Provider" },
                        ...(onlyShow === "transferredbyme"
                          ? [{ label: "Transferred To / When" }]
                          : []),
                        { label: "Vehicle" },
                        { label: "Number" },
                        { label: "Days", key: "days" },
                        { label: "Amount", key: "payment.total_amount" },
                        { label: "Timer" },
                        { label: "PDF" },
                        { label: "Tax status" },
                        { label: "Payment" },
                        { label: "Actions" },
                      ].map(({ label, key }) => (
                        <th
                          key={label}
                          onClick={key ? () => requestSort(key) : undefined}
                          className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide ${key ? "cursor-pointer hover:bg-gray-100 select-none" : ""}`}
                        >
                          {label}
                          {key && <SortIcon col={key} />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map((order) => {
                      const isPending =
                        (order.status_of_Tax || "").toLowerCase() === "pending";
                      const hasPdf = !!order.pdf?.url;
                      const wasReceivedViaTransfer =
                        !!order.wasReceivedViaTransfer;
                      const timerInfo = getTimerInfo(order, now);
                      const isDanger =
                        timerInfo.type === "delayed" && timerInfo.isDanger;
                      return (
                        <tr
                          key={order.id}
                          className={`transition-colors ${isDanger ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50/60"}`}
                        >
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            {fmtDateTime(order.createdAt)}
                          </td>
                          <td className="px-4 py-3">{order.state}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {order.border}
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <span className="font-medium text-gray-900">
                                {order.provider_name || "—"}
                              </span>

                              {order.provider_number ? (
                                <span className="text-xs font-mono text-gray-500">
                                  {order.provider_number}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">
                                  No number
                                </span>
                              )}
                              {wasReceivedViaTransfer && (
                                <ReassignedBadge title="Received via transfer" />
                              )}
                            </div>
                          </td>

                          {onlyShow === "transferredbyme" && (
                            <td className="px-4 py-3 text-xs text-gray-600">
                              {order.my_transfers?.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {order.my_transfers.map((t, i) => (
                                    <div key={i}>
                                      <span className="font-mono">
                                        {t.from_provider}
                                      </span>

                                      <span className="font-mono">
                                        → {t.to_provider}
                                      </span>
                                      <br />
                                      <span className="text-gray-400">
                                        {fmtDateTime(t.transferred_at)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3">{order.vehicle}</td>
                          <td className="px-4 py-3 font-mono text-xs uppercase">
                            {order.vehicleNumber}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {order.days || order.numDays || "—"}
                          </td>
                          <td className="px-4 py-3 font-semibold">
                            {fmtAmt(order.payment?.total_amount)}
                          </td>
                          <td className="px-4 py-3">
                            <OrderTimer order={order} now={now} />
                          </td>
                          <td className="px-4 py-3 text-center">
                            {hasPdf ? (
                              <a
                                href={order.pdf.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="View PDF"
                                className="text-green-600 hover:text-green-800 inline-block"
                              >
                                <FileCheck size={18} />
                              </a>
                            ) : (
                              <FileX
                                size={18}
                                className="text-amber-500 mx-auto"
                              />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              label={order.status_of_Tax || "Unknown"}
                              cls={statusCls(order.status_of_Tax)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              label={order.payment_status || "Unknown"}
                              cls={paymentCls(order.payment_status)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            {order.canEdit === false ? (
                              <div className="flex items-center justify-end">
                                <ActionBtn
                                  icon={<Lock size={16} />}
                                  title="No access — not assigned to you"
                                  cls="text-gray-400 hover:bg-gray-100"
                                  onClick={showNoAccess}
                                />
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 justify-end">
                                <ActionBtn
                                  icon={<Eye size={16} />}
                                  title="View"
                                  cls="text-indigo-600 hover:bg-indigo-50"
                                  onClick={() => setSelectedOrder(order)}
                                />
                                <ActionBtn
                                  icon={<User size={16} />}
                                  title="Driver details"
                                  cls="text-teal-600 hover:bg-teal-50"
                                  onClick={() => showDriverInfo(order.driver)}
                                />
                                <ActionBtn
                                  icon={<Send size={16} />}
                                  title="Transfer"
                                  cls="text-green-600 hover:bg-green-50"
                                  onClick={() => setTransferOrder(order)}
                                />
                                <ActionBtn
                                  icon={<WhatsappIcon className="h-4 w-4" />}
                                  title="WhatsApp Driver"
                                  cls="text-green-600 hover:bg-green-50"
                                  onClick={() =>
                                    openWhatsapp(order.driver, order.contact)
                                  }
                                />
                                <ActionBtn
                                  icon={<Upload size={16} />}
                                  title="Upload PDF"
                                  cls="text-green-600 hover:bg-green-50"
                                  onClick={() => setPdfModalOrder(order)}
                                />
                                {isPending && (
                                  <>
                                    <ActionBtn
                                      icon={<Edit size={16} />}
                                      title="Edit"
                                      cls="text-amber-600 hover:bg-amber-50"
                                      onClick={() =>
                                        Swal.fire(
                                          "Coming soon",
                                          "Edit functionality coming soon",
                                          "info",
                                        )
                                      }
                                    />
                                    <ActionBtn
                                      icon={<X size={16} />}
                                      title="Cancel"
                                      cls="text-red-500 hover:bg-red-50"
                                      onClick={() =>
                                        changeStatus(order.id, "cancelled")
                                      }
                                    />
                                  </>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-3 border-t border-gray-100 text-sm text-gray-500 gap-2">
                <span>
                  Showing {dispStart}–{dispEnd} of {fmt(totalOrders)} orders
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchOrders(1)}
                    disabled={page === 1}
                    className="px-2 py-1.5 rounded text-xs hover:bg-gray-100 disabled:opacity-40"
                  >
                    First
                  </button>
                  <button
                    onClick={() => fetchOrders(page - 1)}
                    disabled={page === 1}
                    className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="px-3 py-1.5 bg-gray-100 rounded-lg font-medium text-xs">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => fetchOrders(page + 1)}
                    disabled={page === totalPages}
                    className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => fetchOrders(totalPages)}
                    disabled={page === totalPages}
                    className="px-2 py-1.5 rounded text-xs hover:bg-gray-100 disabled:opacity-40"
                  >
                    Last
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ViewModal />
      <DriverModal />

      {/* PDF Upload Modal */}
      {pdfModalOrder && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-base font-semibold mb-5">
              Upload / replace PDF
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1.5">
                Document type
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDocType("border_tax")}
                  disabled={uploading}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                    docType === "border_tax"
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  Border Tax PDF
                </button>
                <button
                  type="button"
                  onClick={() => setDocType("refund")}
                  disabled={uploading}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                    docType === "refund"
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  Refund Image/PDF
                </button>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1.5">
                PDF file
              </label>
              <input
                type="file"
                accept="application/pdf,image/*"
                disabled={uploading}
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50"
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-1.5">
                Valid till (optional)
              </label>
              <input
                type="date"
                value={validTill}
                disabled={uploading}
                onChange={(e) => setValidTill(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPdfModalOrder(null)}
                disabled={uploading}
                className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadPdf}
                disabled={uploading || !pdfFile}
                className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Uploading…
                  </>
                ) : (
                  "Upload PDF"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ProviderTransferModal
        open={!!transferOrder}
        order={transferOrder}
        close={() => setTransferOrder(null)}
        onSuccess={() => {
          fetchOrders(page);
          setTransferOrder(null);
        }}
      />
    </div>
  );
};

// ─── TINY SHARED ATOMS ───────────────────────────────────────────────────────

const Chip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
    {label}
    <button onClick={onRemove} className="ml-0.5 hover:text-indigo-900">
      <X size={12} />
    </button>
  </span>
);

const ActionBtn = ({ icon, title, cls, onClick }) => (
  <button
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded-lg transition ${cls}`}
  >
    {icon}
  </button>
);

// ─── 30-MIN TAX TIMER ────────────────────────────────────────────────────────

const TAX_WINDOW_MS = 30 * 60 * 1000;
const DANGER_DELAY_MS = 4 * 60 * 60 * 1000;

const fmtMinSec = (ms) => {
  const totalSec = Math.max(Math.floor(ms / 1000), 0);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${String(s).padStart(2, "0")}s`;
};

const getTimerInfo = (order, now) => {
  const hasPdf = !!order.pdf?.url;
  if (hasPdf) return { type: "done" };

  const created = new Date(order.createdAt).getTime();
  const deadline = created + TAX_WINDOW_MS;
  const diff = deadline - now;

  if (diff > 0) return { type: "countdown", diff };

  const delayMs = Math.abs(diff);
  return {
    type: "delayed",
    diff: delayMs,
    isDanger: delayMs > DANGER_DELAY_MS,
  };
};

const OrderTimer = ({ order, now }) => {
  const info = getTimerInfo(order, now);

  if (info.type === "done") {
    return (
      <span className="text-xs text-green-600 font-medium">PDF Uploaded</span>
    );
  }

  if (info.type === "countdown") {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
        {fmtMinSec(info.diff)} left
      </span>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      {info.isDanger && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white animate-pulse">
          ⚠ Danger
        </span>
      )}
      <span
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold border ${
          info.isDanger
            ? "bg-red-100 text-red-800 border-red-300"
            : "bg-red-50 text-red-700 border-red-100"
        }`}
      >
        Delayed by {fmtMinSec(info.diff)}
      </span>
    </div>
  );
};

export default AllBorderTax;
